// src/pages/Journal/JournalEntry.js
import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Typography,
  Space,
  App,
  Select,
  Grid,
  Flex,
  Upload,
  message,
} from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import InputNumberField from "../../components/Input/InputNumber";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  fetchJournalCode,
  fetchJournals,
  saveJournal,
  fetchHeadAccs,
  fetchExistingJournalCodes,
} from "./journalThunk";
import {
  fetchLoadIntoAccs,
  fetchTagGroups,
  fetchTagGroupFields,
  fetchEmployees,
} from "../../store/commonThunks/lookups";
import SearchListAutoComplete from "../../components/SearchListAutoComplete";
import { useNavigate, useParams } from "react-router-dom";
import {
  dateTimeFormat,
  formatToUtc,
  getAccessType,
  getDateTimeFromSelectedDate,
  objectToFormData,
} from "../../lib/functions";
import {
  setEntryDate,
  setIsEditJournal,
  setSelectedJournal,
  setRefreshList,
  setJournalCode,
  setJournalDate,
} from "./journalSlice";
import InputField from "../../components/Input/Input";
import TagCreateForm from "../Settings/Tags/components/TagCreateForm";
import AddNewModal from "../../components/DropDown/AddNewModal";
import Big from 'big.js';
const { Title } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

// The account picker identifies the chosen option by its text, so accounts
// sharing a name (e.g. a "Sales" sales account and a "Sales" cash account)
// collapse into one and only one of them can be selected. Shared names get the
// account type (and code, if the type is shared too) appended to stay distinct.
const toAccountOptions = (accs) => {
  const list = accs || [];
  const nameKey = (acc) => String(acc.acc_name ?? "").trim().toLowerCase();
  const count = (keyFn, acc) => list.filter((a) => keyFn(a) === keyFn(acc)).length;
  const nameTypeKey = (acc) => `${nameKey(acc)}|${acc.acc_type_id}`;

  return list.map((acc) => {
    let label = acc.acc_name;
    if (count(nameKey, acc) > 1) {
      const parts = [acc.acc_type_name || acc.acc_type_id];
      if (count(nameTypeKey, acc) > 1) parts.push(acc.acc_code || String(acc.acc_id).slice(0, 8));
      label = `${acc.acc_name} (${parts.join(" - ")})`;
    }
    return { sale_id: acc.acc_id, display_text: label, raw: acc };
  });
};

const JournalEntry = ({ authInfo }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { modal } = App.useApp();
  const [form] = Form.useForm();

  const [rows, setRows] = useState([]);
  const [selectedPayIntoAcc, setselectedPayIntoAcc] = useState(null);
  const [tagGroupList, setTagGroupList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [tagModal, setTagModal] = useState(false);
  const [existingInvoiceUrl, setExistingInvoiceUrl] = useState(null);

  let [access_type, access_type_set] = useState("");

  useEffect(() => {
    const access = getAccessType("journal_entry");
    access_type_set(access);
  }, []);

  const isFieldHidden = (fieldKey, pageKey = "journal_entry") => {
    return authInfo?.visibilitytabs?.some(
      (item) => item.page_key === pageKey && item.field_key === fieldKey
    );
  };

  const isPdfInvoice =
    invoiceFile instanceof File
      ? invoiceFile.type === "application/pdf"
      : /\.pdf($|\?)/i.test(existingInvoiceUrl || "");

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setInvoiceFile(file);
      setExistingInvoiceUrl(null);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const {
    journalDate,
    entryDate,
    journalCode,
    headAccs,
    selectedJournal,
    isEditJournal,
    existingJournalCodes,
  } = useSelector((state) => state.journals);
  const { loadIntoAccs } = useSelector((s) => s.lookups);

  useEffect(() => {    
    if (!isEditJournal){ 
      dispatch(fetchJournalCode());
    }
    dispatch(fetchHeadAccs());
    dispatch(fetchEmployees());
    dispatch(fetchLoadIntoAccs());
    dispatch(fetchTagGroups())
      .unwrap()
      .then((groups) =>
        Promise.all(
          groups.map((g) => dispatch(fetchTagGroupFields(g.id)).unwrap())
        )
      )
      .then((allFields) => {
        const combined = allFields.flat().map((item) => ({
          ...item.raw,
          combinedField: item.display_text,
          id: item.id,
        }));
        setTagGroupList(combined);
      })
      .catch(() => {});
    setRows([
      {
        key: Date.now() + Math.random(),
        from_acc_id: 0,
        acc_name: "",
        debit_amount: 0,
        credit_amount: 0,
      },
      {
        key: Date.now() + Math.random(),
        from_acc_id: 0,
        acc_name: "",
        debit_amount: 0,
        credit_amount: 0,
      },
    ]);
  }, [dispatch]);

  useEffect(() => {
    if (id && !selectedJournal) {
      dispatch(fetchJournals())
        .unwrap()
        .then((list) => {
          const found = list.find((r) => r.jrn_id == id);
          if (found) {
            dispatch(setSelectedJournal(found));
            dispatch(setIsEditJournal(true));
          }
        })
        .catch(() => {});
    }
  }, [id, selectedJournal, dispatch]);

  useEffect(() => {
    if (isEditJournal && selectedJournal) {
      dispatch(setJournalCode(selectedJournal.jrn_code));

      form.setFieldsValue({
        jrn_date: dayjs(selectedJournal.created_date),
        description: selectedJournal.narration,
        invoice_no: selectedJournal.invoice_no || "",
      });

      // invoice file (if exists)
      if (selectedJournal.invoice_image) {
        setExistingInvoiceUrl(selectedJournal.invoice_image);
        setPreview(selectedJournal.invoice_image);
        setInvoiceFile({
          uid: "-1",
          name: "Invoice",
          status: "done",
          url: selectedJournal.invoice_image,
        });
      }

      // tags
      const tagValues = selectedJournal.tag_field_id
        ? Array.isArray(selectedJournal.tag_field_id)
        ? selectedJournal.tag_field_id
        : [selectedJournal.tag_field_id]:[];
      setSelectedTags(tagValues);
      form.setFieldsValue({ tags: tagValues });

      // journal rows
      setRows(
        (selectedJournal.details || []).map((d) => ({
          key: d.jrn_d_id || Date.now() + Math.random(),
          from_acc_id: d.acc_id,
          acc_name: d.acc_name,
          debit_amount: d.debit_amount || 0,
          credit_amount: d.credit_amount || 0,
        }))
      );

      dispatch(setEntryDate(dayjs(selectedJournal.created_date).toISOString()));
    }
  }, [isEditJournal, selectedJournal, loadIntoAccs, form, dispatch]);


  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        key: Date.now() + Math.random(),
        from_acc_id: 0,
        acc_name: "",
        debit_amount: 0,
        credit_amount: 0,
      },
    ]);
  };

  const handleRemoveRow = (key) => setRows(rows.filter((r) => r.key !== key));

  const handleCancel = () => {
    dispatch(setSelectedJournal(null));
    dispatch(setIsEditJournal(false));
    navigate("/accounts/journal-entry");
  };

  useEffect(() => {
    dispatch(fetchExistingJournalCodes());
  }, [dispatch]);

  // Save
const onFinish = async (values) => {
  let finalEntryDate = entryDate;
  if (!dayjs(entryDate).isSame(dayjs(), "day")) {
    finalEntryDate = getDateTimeFromSelectedDate(entryDate);
  } else {
    finalEntryDate = entryDate;
  }
  let finalCreatedDate = journalDate;
  if (!dayjs(journalDate).isSame(dayjs(), "day")) {
    finalCreatedDate = getDateTimeFromSelectedDate(journalDate);
  } else {
    finalCreatedDate = journalDate;
  }

  try {
    // ✅ Use big.js for accurate comparison
    const debitTotal = rows
      .reduce((s, r) => s.plus(new Big(Number(r.debit_amount) || 0)), new Big(0));
    const creditTotal = rows
      .reduce((s, r) => s.plus(new Big(Number(r.credit_amount) || 0)), new Big(0));

    // ✅ Use .eq() for Big.js comparison, NOT !==
    if (!debitTotal.eq(creditTotal)) {
      modal.error({ title: "Debit and Credit totals must match." });
      return;
    }

    // ✅ Use Big.js .eq(0) not === 0
    if (rows.length === 0 || debitTotal.eq(0)) {
      modal.error({ title: "Journal rows cannot be empty." });
      return;
    }

    for (let r of rows) {
      if (!r.acc_name) {
        modal.error({ title: "Please select Income Head before saving." });
        return;
      }
    }

    const tags = form.getFieldValue("tags") || [];
    const formData = new FormData();

    formData.append("jrn[jrn_code]", journalCode.trim());
    formData.append("jrn[invoice_no]", values.invoice_no || "");
    formData.append("jrn[created_date]", formatToUtc(finalCreatedDate));
    formData.append("jrn[entry_date]", formatToUtc(finalEntryDate));

    // ✅ Send 12 decimal precision to backend, no float drift
    formData.append("jrn[debit_total]", debitTotal.toFixed(12));
    formData.append("jrn[credit_total]", creditTotal.toFixed(12));

    formData.append("jrn[narration]", values.description || "");
    formData.append("jrn[jrn_id]", isEditJournal ? selectedJournal.jrn_id : 0);
    formData.append("jrn[tag_field_id]", JSON.stringify(tags));
    formData.append("jrn[action]", isEditJournal ? "update" : "create");

    if (invoiceFile instanceof File) formData.append("jrn[photo]", invoiceFile);

    // ✅ Row level amounts - also use toFixed(12)
    rows.forEach((r, idx) => {
      formData.append(`jrnDetail[${idx}][acc_id]`, r.from_acc_id);
      formData.append(`jrnDetail[${idx}][acc_name]`, r.acc_name || "");
      formData.append(`jrnDetail[${idx}][debit_amount]`, new Big(Number(r.debit_amount) || 0).toFixed(12));
      formData.append(`jrnDetail[${idx}][credit_amount]`, new Big(Number(r.credit_amount) || 0).toFixed(12));
    });

    const res = await dispatch(saveJournal(formData)).unwrap();
    modal.success({ title: res.msg || "Journal saved successfully" });


    
    dispatch(setSelectedJournal(null));
    dispatch(setIsEditJournal(false));
    dispatch(setRefreshList(true));
    form.resetFields();
    setRows([]);
    setPreview(null);
    setInvoiceFile(null);
    navigate("/accounts/journal-entry");
  } catch (err) {
    modal.error({ title: err.message || "Failed to save journal" });
  }
};

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 50,
    },
    {
      title: "Account",
      dataIndex: "acc_name",
      render: (val, record, index) => (
        <SearchListAutoComplete
          entityType="accounts"
          style={{ width: "100%" }}
          optionsList={toAccountOptions(headAccs)}
          value={
            record.from_acc_id
              ? {
                  value: record.from_acc_id,
                  display_text: record.acc_name,
                }
              : null
          }
          onSelect={(obj) => {
            const newRows = [...rows];
            newRows[index].from_acc_id = obj?.raw?.acc_id || 0;
            newRows[index].acc_name = obj?.raw?.acc_name || "";
            setRows(newRows);
          }}
          placeholder="Account"
          showAddNew={false}
        />
      ),
    },
    {
      title: "Debit Amount",
      dataIndex: "debit_amount",
      render: (_, record, index) => (
        <InputNumberField
          min={0}
          step={1}
          precision={2}
          value={record.debit_amount}
          style={{ width: "100%" }}
          onFocus={() => {
              const newRows = [...rows];
              newRows[index].debit_amount = undefined;
              setRows(newRows);
            }}
          onChange={(val) => {
            const newRows = [...rows];
            const debitVal = Number(val) || 0;

            // If entering a debit, reset credit
            if (debitVal > 0) {
              newRows[index].credit_amount = 0;
            }

            newRows[index].debit_amount = debitVal;
            setRows(newRows);
          }}
        />
      ),
    },
    {
      title: "Credit Amount",
      dataIndex: "credit_amount",
      render: (_, record, index) => (
        <InputNumberField
          min={0}
          step={1}
          precision={2}
          value={record.credit_amount}
          style={{ width: "100%" }}
           onFocus={() => {
            const newRows = [...rows];
            newRows[index].credit_amount = undefined;
            setRows(newRows);
          }}
          onChange={(val) => {
            const newRows = [...rows];
            const creditVal = Number(val) || 0;

            // If entering a credit, reset debit
            if (creditVal > 0) {
              newRows[index].debit_amount = 0;
            }

            newRows[index].credit_amount = creditVal;
            setRows(newRows);
          }}
        />
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveRow(record.key)}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>Journal / Double Entry</Title>
      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%", maxWidth: "1400px" }}
        variant="filled"
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
        initialValues={{
          entry_date: dayjs(entryDate),
          jrn_date: dayjs(journalDate),
        }}
        onFinish={onFinish}
      >
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            {/* Journal Code */}
            <Form.Item label="Journal Code">
              <Input value={journalCode} disabled />
            </Form.Item>

            {/* Journal Date */}
            <Form.Item
              name="jrn_date"
              label="Journal Date"
              rules={[{ required: true, message: "Please select date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                value={journalDate ? dayjs(journalDate) : null}
                onChange={(d) =>
                  dispatch(setJournalDate(d ? d.toISOString() : null))
                }
                format={dateTimeFormat}
              />
            </Form.Item>

            {/* Entry Date */}
            <Form.Item
              name="entry_date"
              label="Entry Date"
              rules={[{ required: true, message: "Please select date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                value={entryDate ? dayjs(entryDate) : null}
                onChange={(d) =>
                  dispatch(setEntryDate(d ? d.toISOString() : null))
                }
                format={dateTimeFormat}
                disabled
              />
            </Form.Item>
          </Flex>

          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            {/* Invoice No */}
            <Form.Item name="invoice_no" label="Invoice No.">
              <InputField
                placeholder="Invoice No."
                data-testid="input-invoice-no"
              />
            </Form.Item>

            {/* Upload Invoice */}
            {!isFieldHidden("Upload Invoice") && (
              <Form.Item
                label="Upload Invoice"
                name="invoice_image"
                className="upload-block-wrap"
              >
                <div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,application/pdf"
                    onChange={handleImageChange}
                    style={{ marginBottom: "10px" }}
                  />
                  {preview && (
                    <div style={{ marginTop: 8 }}>
                    {isPdfInvoice ? (
                      <a
                        href={existingInvoiceUrl || preview}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "block" }}
                      >
                        {invoiceFile instanceof File ? invoiceFile.name : "View PDF invoice"}
                      </a>
                    ) : (
                      <img
                        src={preview}
                        alt="Invoice"
                        style={{ maxWidth: "300px", border: "1px solid #ccc" }}
                      />
                    )}
                    {existingInvoiceUrl && (
                      <Typography.Text type="secondary">
                        Existing invoice
                      </Typography.Text>
                    )}
                    </div>
                  )}
                </div>
              </Form.Item>
            )}

            {/* Tags */}
            <Form.Item name="tags" label="Tags">
              <Select
                mode="multiple"
                maxTagCount="responsive"
                className="tag-multiselect"
                allowClear
                style={{ width: "100%" }}
                placeholder="Choose Tag Groups"
                options={tagGroupList.map((item) => ({
                  value: item.id,
                  label: item.combinedField,
                }))}
                onChange={(ids) => setSelectedTags(ids)}
                popupRender={(menu) => (
                  <>
                    {menu}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: 8,
                        borderTop: "1px solid #f0f0f0",
                      }}
                    >
                      <Button type="link" onClick={() => setTagModal(true)}>
                        + Add New Tag
                      </Button>
                    </div>
                  </>
                )}
              />
            </Form.Item>
          </Flex>
        </Flex>

        {/* Add Row Button */}
        <Button
          type="dashed"
          onClick={handleAddRow}
          style={{ marginBottom: 12 }}
          icon={<PlusOutlined />}
        >
          Add New
        </Button>

        {/* Journal Rows Table */}
        <Table
          dataSource={rows}
          columns={columns}
          pagination={false}
          rowKey="key"
          bordered
          style={{ marginTop: 16, marginBottom: 24 }}
          scroll={{ x: "max-content" }}
          footer={() => {
            const debitTotal = rows.reduce(
              (s, r) => s + Number(r.debit_amount || 0),
              0
            );
            const creditTotal = rows.reduce(
              (s, r) => s + Number(r.credit_amount || 0),
              0
            );
            return (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <strong>
                  Debit Total: {debitTotal.toFixed(2)} | Credit Total:{" "}
                  {creditTotal.toFixed(2)}
                </strong>
              </div>
            );
          }}
        />

        <Form.Item name="description" label="Description">
          <TextArea rows={3} />
        </Form.Item>

        <Space style={{ marginTop: 16 }}>
          <Button onClick={handleCancel}>Back</Button>
          <Button
            onClick={() => {
              form.resetFields();
              setRows([]);
              setInvoiceFile(null);
            }}
          >
            Reset
          </Button>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                htmlType="submit"
                data-testid="btn-save-journal"
              >
                Save Journal
              </Button>
            </>
          )}
        </Space>
      </Form>
      <AddNewModal
        open={tagModal}
        title="Add Tag"
        onClose={() => setTagModal(false)}
      >
        <TagCreateForm
          authInfo={authInfo}
          onSuccess={async ({ createdName }) => {
            const res = await dispatch(fetchTagGroupFields("")).unwrap();

            const match = res.find(
              (t) =>
                t.raw.field_name.toLowerCase() === createdName.toLowerCase()
            );

            if (!match) {
              message.warning("Tag creation failed or tag not found");
              return;
            }

            // 🔥 UPDATE OPTIONS FIRST
            setTagGroupList((prev) => {
              const exists = prev.some((p) => p.id === match.id);
              return exists
                ? prev
                : [
                    ...prev,
                    {
                      ...match.raw,
                      combinedField: match.display_text,
                      id: match.id,
                    },
                  ];
            });

            const current = form.getFieldValue("tags") || [];

            if (current.includes(match.id)) {
              message.info("Tag already exists and is already selected");
              return;
            }

            // ✅ NOW auto-select
            form.setFieldsValue({
              tags: [...current, match.id],
            });
            setSelectedTags([...current, match.id]);

            message.success("Tag added");
            setTagModal(false);
          }}
        />
      </AddNewModal>
    </div>
  );
};

export default JournalEntry;
